import React, { Component, ErrorInfo, ReactNode } from 'react';
import { theme } from '../theme';

interface Props {
  children: ReactNode;
  /** Optional fallback renderer; defaults to a generic recovery card. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /** Optional label so logs say which boundary tripped. */
  scope?: string;
}

interface State {
  error: Error | null;
}

/**
 * Defensive boundary so a bug in one screen/modal does not blank the whole app.
 * Wrap risky islands (chat modals, third-party widgets) or place once at App root.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error(
      `[ErrorBoundary${this.props.scope ? `:${this.props.scope}` : ''}]`,
      error,
      info.componentStack,
    );
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div
        style={{
          minHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 24,
          textAlign: 'center',
          color: theme.colors.text,
          fontFamily: theme.fonts.body,
          background: theme.colors.background,
        }}
      >
        <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
        <h2
          style={{
            fontFamily: theme.fonts.heading,
            color: theme.colors.text,
            marginBottom: 8,
          }}
        >
          Algo salió mal
        </h2>
        <p
          style={{
            color: theme.colors.textSecondary,
            maxWidth: 480,
            marginBottom: 20,
            lineHeight: 1.5,
          }}
        >
          La pantalla tuvo un problema inesperado. Podés reintentar o recargar la página.
          Si vuelve a pasar, escribinos a{' '}
          <a href="mailto:futboleroapp@gmail.com" style={{ color: theme.colors.primary }}>
            futboleroapp@gmail.com
          </a>
          .
        </p>
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            type="button"
            onClick={this.reset}
            style={{
              padding: '10px 20px',
              background: theme.colors.primary,
              color: '#000',
              border: 'none',
              borderRadius: 8,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: theme.fonts.heading,
            }}
          >
            Reintentar
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 20px',
              background: 'transparent',
              color: theme.colors.text,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: 8,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Recargar
          </button>
        </div>
      </div>
    );
  }
}
